// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

// Responsive width of the assistant side panel: clamp(320px, 38vw, 420px) — 420px on a normal desktop,
// shrinking to 38vw on smaller screens (never below a 320px readable floor) so the map/pano keep room.
// A CSS length STRING (not a number), used directly as width, inside calc(), and as the
// --khora-drawer-offset CSS variable: the chatbot sets that variable when the drawer opens, and the app
// frame (shell/styles.ts) reads it to shift content left by the same amount so the panel sits beside it
// instead of overlaying it.
const CHATBOT_DRAWER_WIDTH = 'clamp(320px, 38vw, 420px)';

export default CHATBOT_DRAWER_WIDTH;

// MASTER kill switch for the entire Khora integration — the single, proactive off-switch. Set to false
// and the user-facing feature cleanly disappears in one place: the launcher/drawer and the dashboard
// don't mount, and the viewer's chat→map-highlight + Dashboard-layer-sync effects don't run. The one
// behavioral change to a SHARED, pre-existing file — the Create-Order dialog's focus relax — also
// reverts to the pre-Khora behavior. NOT reverted by the switch: the PopupWindow "Pole Image" http(s)
// link hardening (kept always-on; behavior-preserving for real image URLs); a few dispatches still
// write to the otherwise-unused Khora Redux slices as harmless no-ops; and the code is disabled, not
// tree-shaken, so it still ships in main.js. Default true (feature on). Two finer gates apply on top:
// the chat is limited to the pilot project (PILOT_PROJECT_NAME) and can be turned off per-session with
// ?khora=off. The production rollout switch is the khoraChatbot LaunchDarkly flag; this is the hard,
// in-code safety valve.
export const KHORA_ENABLED = true;

// PILOT gate: while Khora is piloted it is enabled ONLY for the project named EXACTLY
// 'Wilkes Barre' — mirrors the server's khora middleware, which is the enforcing side (the client
// gate only hides UI). Keep the string in sync with the server's PILOT_PROJECT_NAME and the pilot
// project's actual name. Replace with the khoraChatbot LaunchDarkly flag (strict `=== true`) for
// the real rollout.
export const PILOT_PROJECT_NAME = 'Wilkes Barre';

// THE single mount gate for every Khora surface: the launcher/drawer AND its viewer companions
// (the "Analyze with Khora" pano chip with its looking-angle readout, the gdb-rows bottom table,
// the "Select area" region chip, and the output Dashboard). Combines the master kill switch, the
// pilot-project gate, and the ?khora=off diagnostic escape hatch, so a non-pilot project — or an
// A/B run with ?khora=off — mounts NO Khora UI at all, not just no launcher.
export const isKhoraUiEnabled = (projectName: string | undefined): boolean => {
  const forcedOff = new URLSearchParams(window.location.search).get('khora') === 'off';
  return KHORA_ENABLED && !forcedOff && projectName === PILOT_PROJECT_NAME;
};

// Base URL override for the aw-server that serves the gdb-chat route
// (POST /orders/:orgid/:pid/:oid/gdb-chat, which invokeLambda's the chat Lambda). EMPTY in the
// repo — the chat follows the environment's Config.API_URL. For local development against a
// locally-running aw-server, set this to 'http://localhost:3000' in your working copy (never
// commit it).
export const AW_SERVER_CHAT_BASE_URL = '';

// Khora binary assets served from the airworks-website-assets-prod bucket via the content.airworks.io
// CDN (same host as the sign-in background video) — NOT bundled, so they can be swapped without a
// client rebuild and don't bloat main.js. To update an asset, replace the S3 object (public-read).
export const KHORA_ICON_URL = 'https://content.airworks.io/khora/khora-icon-midnight-512.png';
export const KHORA_TOUR_VIDEO_URL = 'https://content.airworks.io/khora/khora-tour.mp4';
